<template>
  <section id="message-customer" v-if="message && message.customer">
    <div class="text-small" v-if="message.message === '<Tagged>'">
      <hr :class="['hr-text']" :data-content="convertContentHrLine(message)">
    </div>
    <div class="d-flex justify-content-start align-items-center pb-4" :style="quotedRowStyle" v-else>
      <div class="pr-3">
        <div class="image-cropper-md fade-in">
          <img
            v-lazy="message.customer.profile_picture"
            class="image-profile img-circle"
          />
        </div>
      </div>
      <div class="w-75">
        <div class="text-chat-time pb-1">
          {{ message.customer.name | firstName }} |
          {{ message.created_at | chatDateTime }}
        </div>
        <!-- A message with no quote must render exactly as it did before this feature, so both
             branches are a v-if/v-else PAIR whose v-else arm is the untouched pre-feature markup.
             A bare v-if would leave a comment placeholder in every non-reply bubble.
             NEVER add a <style scoped> block to this file: it would stamp a data-v-* onto ~20
             legacy nodes on every message. That is why the host's rules live in their own SFC. -->
        <div v-if="message.attachments && message.attachments.length > 0">
          <QuoteBubbleHostV2026 v-if="quoteHost" key="qm-host" :quotedMessage="message.quoted_message">
            <message-attachment
            @onClickAttachment="selectAttachment"
            @onSelectAttachment="selectAttachmentedFile"
            :selectedFile="selectedFile"
            :message="message"
            ></message-attachment>
          </QuoteBubbleHostV2026>
          <!-- Keyed siblings, never two independent v-ifs: exactly ONE <message-attachment> per
               message. Two would emit @load twice for one attachment and kill auto-scroll app-wide. -->
          <message-attachment
          v-else
          key="qm-plain"
          @onClickAttachment="selectAttachment"
          @onSelectAttachment="selectAttachmentedFile"
          :selectedFile="selectedFile"
          :message="message"
          ></message-attachment>
        </div>
        <div v-else>
          <div class="card customer-chat border-0 mw-100 d-inline-block mr-2 p-0">
              <!-- No key on this pair, deliberately: Vue then patches in place instead of
                   destroying the subtree, which preserves ThaiTranslation's local state. -->
              <div class="card-body" style="white-space: pre-wrap; padding: 0px;" v-if="showQuote">
                <QuotedMessageV2026 :quotedMessage="message.quoted_message" style="border: none !important;" />
                <div class="text-small text-reset text-decoration-none p-2" v-html="message.message" v-linkified></div>
                <thai-translation :message="message.message" class="p-2" />
              </div>
              <div class="card-body p-2" style="white-space: pre-wrap;" v-else>
                <div class="text-small text-reset text-decoration-none" v-html="message.message" v-linkified></div>
                <thai-translation :message="message.message" />
              </div>
            </div>
          <div class="mt-3 d-flex justify-content-start align-content-center">
            <div class="text-remark mr-2" v-if="adminIds.includes(admin.id) && !isHideOnRecruitment">
              <button class="btn btn-outline-primary rounded-pill btn-sm" @click="sendMessageToAiAssistant(message.message)">
                <i class="fa fa-comment-alt"></i> แชทถาม AI
              </button>
            </div>
            <div v-if="departmentIds.includes(admin.department_id) && !isHideOnRecruitment">
              <button class="btn btn-outline-info rounded-pill btn-sm" @click="addMultiMessages(message.message)">
                <i class="fa fa-file-export"></i> ให้ AI สกัดข้อมูลจอง
              </button>
            </div>
          </div>
        </div>

        <div class="pt-2 text-small" v-if="message.is_incomplete">
          <span v-if="!isLoading">
            <a href="" @click.prevent="reloadMessage(message)">
              <i class="fas fa-sync-alt"></i> ลองโหลดข้อความอีกครั้ง
            </a>
          </span>
          <span v-else>
            <i class="fas fa-sync fa-spin"></i> กำลังโหลด...
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import chatHelpers from '../../helpers/chatHelpers'
import MessageAttachment from './MessageAttachment'
import ThaiTranslation from './components/ThaiTranslation'
import QuotedMessageV2026 from './QuotedMessageV2026'
import QuoteBubbleHostV2026 from './QuoteBubbleHostV2026'
import axios from 'axios'
import moment from 'moment'

// Fresh token per press, so queueing the same text twice still fires the v2026 panels' watcher.
let fcToken = 0

export default {
  props: {
    message: Object,
    selectedFile: Array
  },
  components: {
    MessageAttachment,
    ThaiTranslation,
    QuotedMessageV2026,
    QuoteBubbleHostV2026
  },
  data () {
    return {
      // Twin of the const in chat-server/helpers/quotedMessageHelper.js; flipped together by hand.
      useInboundQuotePreview: true,
      // Attachment-branch guard, latched on message.id by the watcher below — not a live computed
      // and not a one-time freeze. Live: a flip on a mounted message remounts the <img>, which
      // emits @load a second time for one counted attachment and kills auto-scroll app-wide.
      // Frozen: instances are reused across different messages on pagination, so it goes stale.
      quoteHost: false,
      isLoading: false,
      isTranslating: false,
      tranlatedText: null
    }
  },
  watch: {
    // immediate:true, so quoteHost is decided before the first render and before every re-render
    // that a prop change triggers.
    'message.id': {
      immediate: true,
      handler () {
        this.quoteHost = !!(this.useInboundQuotePreview && this.message &&
                            this.message.quoted_message && this.message.quoted_message.state)
      }
    }
  },
  computed: {
    ...mapState('admin', ['admin']),
    ...mapState('aiAssistant', ['adminIds', 'departmentIds']),
    ...mapState('conversation', ['customerType']),
    isHideOnRecruitment () {
      return ['recruitment_customer', 'recruitment_professional'].includes(this.customerType)
    },
    // Live, unlike quoteHost: the text branch mounts no image, so a flip cannot reach the counter.
    showQuote () {
      return !!(this.useInboundQuotePreview && this.message &&
                this.message.quoted_message && this.message.quoted_message.state)
    },
    hasAttachments () {
      return !!(this.message && this.message.attachments && this.message.attachments.length > 0)
    },
    // MUST return null, never {} or '': only then does Vue skip the element entirely and leave a
    // non-reply row with no style attribute at all. !important is required because bootstrap's
    // .align-items-center is itself !important.
    quotedRowStyle () {
      const on = this.hasAttachments ? this.quoteHost : this.showQuote
      return on ? { 'align-items': 'flex-end !important' } : null
    }
  },
  mounted () {
    this.$store.commit('aiAssistant/SET_REPLY_TRANSLATE_AI', null)
  },
  methods: {
    ...mapActions('aiAssistant', ['translateToThai']),
    hasNoThai (text) {
      return !/[\u0E00-\u0E7F]/.test(text)
    },
    async translateToThai (text) {
      this.isTranslating = true
      this.tranlatedText = null
      const url = '/ai-assistant/translate-to-thai'
      const { data } = await axios.post(url, { user_input: text })
      this.isTranslating = false
      console.log('check data', data)
      if (data) {
        this.tranlatedText = data.translatedText
      }
    },
    sendMessageToAiAssistant (message) {
      if (this.customerType === 'recruitment_customer' || this.customerType === 'recruitment_professional') {
        this.$store.commit('aiAssistant/SET_ASSISTANT_FEATURE', 'translate')
      } else {
        this.$store.commit('aiAssistant/SET_ASSISTANT_FEATURE', 'chat')
      }

      this.$store.commit('aiAssistant/SET_CUSTOMER_MESSAGE', message)
    },
    addMultiMessages (message) {
      this.$store.commit('aiAssistant/SET_ASSISTANT_FEATURE', 'function_calling')
      this.$store.commit('aiAssistant/ADD_MULTI_MESSAGES', message)
      this.$store.commit('aiAssistant/SET_FC_REQUEST', { token: ++fcToken, text: message })
    },
    isChatTopicTag (message) {
      return message.message.charAt(0) === '#'
    },
    async reloadMessage (message) {
      this.isLoading = true
      const url = '/line/reload-message'
      const params = {
        message_id: message.id
      }
      const res = await axios.get(url, { params })
      this.isLoading = false
      if (res.status === 200 && res.data.isUpdated) {
        this.$store.commit('message/UPDATE_NEW_MESSAGE', res.data.message)
      }
    },
    selectAttachment (attachment) {
      this.$emit('onSelectAttachment', attachment)
    },
    selectAttachmentedFile (attachment, isChecked) {
      this.$emit('onSelectAttachmentFile', attachment, isChecked)
    },
    getUrlExtension (message) {
      return chatHelpers.getUrlExtension(message.attachments[0].url)
    },
    convertContentHrLine (message) {
      const customerName = this.$options.filters.firstName(message.customer.name)
      return `${customerName} เลือกหัวข้อ ${message.tag_message} ${moment(message.created_at).format('DD/MM/YYYY HH:mm')}`
    },
    cssMediaType (message) {
      let cssClass = 'card text-item'
      if (message.attachments && message.attachments.length > 0) {
        const ext = this.getUrlExtension(message)
        switch (ext) {
          case 'jpg':
          case 'gif':
          case 'png':
            cssClass = 'card image-item'
            break
          case 'mp4':
          case 'mp3':
          case 'location':
            cssClass = 'card media-item'
            break
        }
      }
      return cssClass
    }
  }
}
</script>
